import React from "react";
import { FlightDetails } from "../types";
import { formatDateTime } from "utils/dateUtils";
import { getAirportOffsetLabel } from "utils/airportTimezones";
import DefaultAirLogo from "../../../assets/images/logo.svg";

interface FlightCardProps {
  flight: FlightDetails;
  isSelected: boolean;
  readOnly?: boolean;
  onSelect: (flight: FlightDetails) => void;
}

const statusColorFor = (flight: FlightDetails) => {
  const s = flight.liveStatus;
  if (s === "Landed" || s === "Arrived")
    return { bg: "bg-green-500", text: "text-white" };
  if (s === "Cancelled") return { bg: "bg-red-500", text: "text-white" };
  if (s === "Early") return { bg: "bg-blue-500", text: "text-white" };
  if (flight.delayMinutes && flight.delayMinutes > 0)
    return { bg: "bg-orange-500", text: "text-white" };
  return { bg: "bg-blue-500", text: "text-white" };
};

const FlightCard: React.FC<FlightCardProps> = ({
  flight,
  isSelected,
  readOnly = false,
  onSelect,
}) => {
  const depTime =
    flight.actualDeparture || flight.estimatedDeparture || flight.departureTime;
  const arrTime = flight.estimatedArrival || flight.arrivalTime;
  const liveDurMin = flight.liveDurationMinutes;
  const durationText =
    liveDurMin != null && liveDurMin > 0
      ? `${Math.floor(liveDurMin / 60)}h ${liveDurMin % 60}m`
      : flight?.elptim
        ? `${parseInt(flight.elptim.slice(0, 3), 10)}h ${parseInt(flight.elptim.slice(3, 5), 10)}m`
        : "";
  const statusColors = statusColorFor(flight);

  return (
    <div
      className={`relative bg-white rounded-xl p-4 border transition-all ${
        readOnly ? "" : "cursor-pointer"
      } ${
        !readOnly && isSelected
          ? "border-[#FFD700] shadow-lg"
          : "border-gray-100" + (readOnly ? "" : " hover:border-gray-200")
      } mt-3`}
      onClick={() => {
        if (readOnly) return;
        onSelect(flight);
      }}
    >
      {/* Floating status pill at top-left */}
      {flight.liveStatus && (
        <span
          className={`absolute -top-3 left-4 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-medium shadow-sm ${statusColors.bg} ${statusColors.text}`}
        >
          <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          {flight.liveStatus}
          {flight.delayMinutes != null && flight.delayMinutes !== 0 && (
            <span className="ml-0.5">
              {flight.delayMinutes > 0
                ? `+${flight.delayMinutes}m`
                : `${flight.delayMinutes}m`}
            </span>
          )}
        </span>
      )}
      <div className="flex flex-wrap items-center gap-2 md:gap-4">
        <div className="flex items-center gap-2 md:gap-3 min-w-0 md:min-w-[100px] md:max-w-[140px] flex-shrink">
          <img
            className="w-9 h-9 md:w-10 md:h-10 object-contain rounded-full flex-shrink-0"
            src={flight.airlineLogo || DefaultAirLogo}
            alt={flight.airlineName}
            onError={(e) => {
              e.currentTarget.src = DefaultAirLogo;
            }}
            loading="lazy"
          />
          <div className="min-w-0">
            <p className="text-grease-100 text-sm font-semibold leading-tight truncate">
              {flight.airlineName}
            </p>
            <p className="text-gray-500 text-xs mt-0.5 truncate">
              {flight.flightNumber}
            </p>
          </div>
        </div>
        {/* Terminal + Gate inline */}
        {(flight.terminal || flight.gate) && (
          <div className="flex items-stretch gap-3 flex-shrink-0">
            {flight.terminal && (
              <div className="flex flex-col">
                <span className="text-gray-400 text-[10px] leading-tight">
                  Terminal
                </span>
                <span className="text-grease-100 text-sm font-semibold leading-tight">
                  {flight.terminal}
                </span>
              </div>
            )}
            {flight.terminal && flight.gate && (
              <div className="w-px bg-gray-200 self-stretch" />
            )}
            {flight.gate && (
              <div className="flex flex-col">
                <span className="text-gray-400 text-[10px] leading-tight">
                  Gate
                </span>
                <span className="text-grease-100 text-sm font-semibold leading-tight">
                  {flight.gate}
                </span>
              </div>
            )}
          </div>
        )}
        {/* Timeline group: departure / route / arrival stay together and, when
            the card is too narrow (mobile), wrap to their own full-width line
            instead of overlapping. On wider screens it fits inline as before. */}
        <div className="flex items-center gap-2 md:gap-4 flex-1 min-w-[220px]">
        <div className="flex flex-col items-center min-w-[48px] md:min-w-[60px] flex-shrink-0">
          <div className="flex items-center gap-1">
            <i className="st-top-right text-gray-500 text-[8px]" />
            <p className="text-gray-500 text-xs">
              {formatDateTime(depTime, {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })}
            </p>
          </div>
          {getAirportOffsetLabel(flight.departureCode, depTime) && (
            <p className="text-gray-400 text-[9px] leading-tight">
              {getAirportOffsetLabel(flight.departureCode, depTime)}
            </p>
          )}
          <p className="text-grease-100 text-base font-bold mt-0.5">
            {flight.departureCode}
          </p>
          <p className="text-gray-500 text-[10px] mt-0.5 truncate max-w-[60px] text-center">
            {flight.departureAirportName?.split(" ")[0]}
          </p>
        </div>
        <div className="flex flex-col items-center flex-1 min-w-0 px-1 md:px-2">
          <div className="flex items-center w-full">
            <div className="w-2 h-2 rounded-full border-2 border-light-yellow flex-shrink-0" />
            <div className="flex-1 border-b-2 border-dashed border-gray-300 mx-1" />
            <i className="st-flight-slim text-grease-100 text-sm mx-1" />
            <div className="flex-1 border-b-2 border-dashed border-gray-300 mx-1" />
            <div className="w-2 h-2 rounded-full border-2 border-light-yellow flex-shrink-0" />
          </div>
          <p className="text-gray-500 text-[10px] text-center mt-1">
            {durationText}
          </p>
          {flight?.stops > 0 && (
            <p className="text-orange-500 text-[10px] text-center">
              {flight.stops} {flight.stops === 1 ? "Stop" : "Stops"}
              {flight.intermediateAirports &&
                flight.intermediateAirports.length > 0 && (
                  <span
                    title={flight.intermediateAirports
                      .map((a) => a.name || a.code)
                      .join(", ")}
                  >
                    {" · via "}
                    {flight.intermediateAirports
                      .map((a) => a.code)
                      .join(", ")}
                  </span>
                )}
            </p>
          )}
        </div>
        <div className="flex flex-col items-center min-w-[48px] md:min-w-[60px] flex-shrink-0">
          <div className="flex items-center gap-1">
            <i className="st-bottom-right text-gray-500 text-[8px]" />
            <p className="text-gray-500 text-xs">
              {formatDateTime(arrTime, {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })}
              {flight.arrday && Number(flight.arrday) > 0 && (
                <span className="ml-0.5 text-[10px] font-medium text-red-500">
                  +{flight.arrday}
                </span>
              )}
            </p>
          </div>
          {getAirportOffsetLabel(flight.arrivalCode, arrTime) && (
            <p className="text-gray-400 text-[9px] leading-tight">
              {getAirportOffsetLabel(flight.arrivalCode, arrTime)}
            </p>
          )}
          <p className="text-grease-100 text-base font-bold mt-0.5">
            {flight.arrivalCode}
          </p>
          <p className="text-gray-500 text-[10px] mt-0.5 truncate max-w-[60px] text-center">
            {flight.arrivalAirportName?.split(" ")[0]}
          </p>
        </div>
        </div>
      </div>
    </div>
  );
};

export default FlightCard;
